/**
 * National Driving Guild — Learner-focused landing page.
 *
 * Audience: Learner drivers searching for the right instructor first time.
 * Headline promise: "Find the right instructor first time" + Best Match quiz CTA.
 *
 * Sections:
 *  1. Header (shared brand bar with audience switcher inline)
 *  2. Hero — promise + Best Match quiz CTA + supporting bullets
 *  3. Audience switcher chip row (small persistent prompt)
 *  4. "What learners worry about" pain pills
 *  5. "Why learners choose NDG" — 6 feature cards focused on learner concerns
 *  6. How It Works — 3 steps (Quiz → Match → Book)
 *  7. Specialisms strip (manual/automatic, female instructor, nervous learner, etc.)
 *  8. Final CTA card
 *  9. Trust footer
 *
 * Visual language matches the main Landing — premium black/gold/white.
 */
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import { useReveal } from "../hooks/useReveal";
import { useSectionEngagement } from "../hooks/useSectionEngagement";
import { trackCta } from "../utils/analytics";
import {
  ArrowRight, ShieldCheck, Sparkles, Star, Heart, MapPin, Lock,
  HeartHandshake, Rocket, Search, Calendar, MessageSquare, Car,
  UserCheck, BadgeCheck, Compass, Zap, ChevronRight, Quote,
  GraduationCap, Eye, Users,
} from "lucide-react";

/* Shared NDG brand shield (kept in sync with Landing.js). */
function NdgShield({ size = 42 }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="0 0 40 44" aria-hidden className="flex-shrink-0">
      <defs>
        <linearGradient id="ndg-gold-learner" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F3E5AB" />
          <stop offset="0.5" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#A37B1F" />
        </linearGradient>
      </defs>
      <path d="M20 1 L37 6 V20 C37 30 30 38 20 42 C10 38 3 30 3 20 V6 Z" fill="#0a0a0a" stroke="url(#ndg-gold-learner)" strokeWidth="1.4" />
      <text x="20" y="18" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="9" fill="url(#ndg-gold-learner)" letterSpacing="0.5">NDG</text>
      <g fill="url(#ndg-gold-learner)">
        <polygon points="11,28 12,30.5 14.5,30.5 12.5,32 13.3,34.5 11,33 8.7,34.5 9.5,32 7.5,30.5 10,30.5" />
        <polygon points="20,28 21,30.5 23.5,30.5 21.5,32 22.3,34.5 20,33 17.7,34.5 18.5,32 16.5,30.5 19,30.5" />
        <polygon points="29,28 30,30.5 32.5,30.5 30.5,32 31.3,34.5 29,33 26.7,34.5 27.5,32 25.5,30.5 28,30.5" />
      </g>
    </svg>
  );
}

function Reveal({ children, delay = 0 }) {
  const [ref, on] = useReveal();
  return (
    <div ref={ref} className={`reveal ${on ? "reveal-in" : ""}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
}

export default function LandingLearner() {
  const { user } = useAuth();
  const primaryHref = user ? "/dashboard" : "/register?role=learner";

  return (
    <div className="min-h-dvh no-overflow-x bg-[#050505] text-white font-outfit antialiased overflow-x-hidden" data-testid="landing-learner">
      <div className="fixed inset-0 grain-overlay opacity-[0.05] pointer-events-none z-0" aria-hidden />
      <Header user={user} primaryHref={primaryHref} />
      <main className="relative">
        <Hero primaryHref={primaryHref} />
        <Reveal><AudienceSwitcher current="learner" /></Reveal>
        <Reveal><LearnerWorries /></Reveal>
        <Reveal><WhyLearners primaryHref={primaryHref} /></Reveal>
        <Reveal><HowItWorks /></Reveal>
        <Reveal><SpecialismsStrip /></Reveal>
        <Reveal><LearnerTestimonial /></Reveal>
        <Reveal><FinalCta primaryHref={primaryHref} /></Reveal>
        <Reveal><TrustFooter /></Reveal>
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HEADER                                                              */
/* ─────────────────────────────────────────────────────────────────── */

function Header({ user, primaryHref }) {
  return (
    <header className="sticky top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.07]">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <NdgShield size={42} />
          <div className="hidden sm:block leading-tight">
            <div className="font-condensed text-white text-lg md:text-xl tracking-wider">
              NATIONAL<br className="md:hidden" /> <span className="md:inline">DRIVING GUILD</span>
            </div>
            <div className="text-[8.5px] md:text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
              For Learners · Find your right-fit instructor
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-300">
          <Link to="/for-learners" data-testid="nav-learners-active" className="text-white border-b-2 border-[#D4AF37] pb-0.5">For Learners</Link>
          <Link to="/" data-testid="nav-instructors" className="hover:text-white transition">For Instructors</Link>
          <Link to="/for-pdis" data-testid="nav-pdis" className="hover:text-white transition">For PDIs</Link>
          <Link to="/marketplace" data-testid="nav-marketplace" className="hover:text-white transition">Find Instructor</Link>
          <Link to="/pricing?audience=learner" data-testid="nav-pricing" className="hover:text-white transition">Pricing</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard">
              <Button data-testid="go-to-dashboard-btn" className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md px-4 md:px-5 h-10 text-sm guild-cta-glow">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button data-testid="login-btn" variant="outline" className="bg-transparent border-white/25 text-white hover:bg-white/5 font-semibold rounded-md h-10 px-4 md:px-5 text-sm">
                  Log In
                </Button>
              </Link>
              <Link to={primaryHref}>
                <Button data-testid="signup-btn" className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md h-10 px-4 md:px-5 text-sm guild-cta-glow">
                  Join Free
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HERO                                                                */
/* ─────────────────────────────────────────────────────────────────── */

const HERO_BULLETS = [
  { icon: Search, title: "Best Match quiz", body: "60-second personality + needs match — get your top 3 instructors." },
  { icon: Heart, title: "Nervous learner support", body: "Filter for patient, calm, anxiety-aware instructors." },
  { icon: Car, title: "Manual or automatic", body: "Both options, clearly labelled. No phone calls to find out." },
  { icon: UserCheck, title: "Female instructor option", body: "Comfort matters. Filter and find one near you." },
  { icon: BadgeCheck, title: "Verified & vetted", body: "Every NDG instructor is DVSA-checked and reviewed." },
  { icon: Calendar, title: "Easy booking", body: "Message, book, and pay all in one place." },
];

function Hero({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("learner_hero");
  return (
    <section ref={sectionRef} data-testid="learner-hero-section" className="relative pt-12 md:pt-14 pb-12 md:pb-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div data-testid="learner-hero-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.07] px-3 py-1 text-[10.5px] uppercase tracking-[0.24em] text-[#F3E5AB] font-semibold mb-6">
            <GraduationCap className="w-3 h-3" /> For learners
          </div>

          <h1 data-testid="learner-hero-title" className="font-display font-semibold text-white tracking-tight leading-[1.02]" style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.5rem)" }}>
            Find the right<br />driving instructor —<br />
            <span className="text-gold-gradient">first time.</span>
          </h1>

          <CoreJourneyMantra className="mt-5" testid="learner-hero-mantra" />

          <p data-testid="learner-hero-subheadline" className="mt-6 md:mt-7 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl">
            Take our 60-second Best Match quiz and we'll line up verified, vetted instructors near you who fit how you learn — patient, fast-track, manual, automatic, female, anxiety-aware. No more guessing.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/marketplace/match" onClick={() => { markCtaClicked({ cta: "best_match_quiz" }); trackCta("learner_landing_quiz_cta", "Best Match quiz", { destination: "/marketplace/match" }); }}>
              <Button data-testid="learner-hero-quiz-btn" className="h-13 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto">
                Take the Best Match quiz <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/marketplace" onClick={() => { markCtaClicked({ cta: "browse_instructors" }); trackCta("learner_landing_browse_cta", "Browse instructors", { destination: "/marketplace" }); }}>
              <Button data-testid="learner-hero-browse-btn" variant="outline" className="h-13 px-6 rounded-md bg-transparent border border-[#D4AF37]/45 hover:bg-[#D4AF37]/[0.06] text-white text-[14px] font-semibold w-full sm:w-auto">
                <Search className="w-4 h-4 mr-2 text-[#D4AF37]" /> Browse instructors
              </Button>
            </Link>
          </div>

          <p data-testid="learner-hero-trust-line" className="mt-5 text-[13px] text-zinc-400 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>
              <span className="text-white font-semibold">Verified instructors only.</span> DVSA-checked. Reviewed by real pupils.
            </span>
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-3.5">
            {HERO_BULLETS.map(({ icon: Icon, title, body }, i) => (
              <div key={i} data-testid={`learner-hero-bullet-${i}`} className="flex items-start gap-3 rounded-xl bg-white/[0.025] border border-white/[0.06] p-3.5">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-[13.5px] font-semibold text-white leading-tight">{title}</div>
                  <div className="text-[12px] text-zinc-400 mt-0.5 leading-snug">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — illustrative card */}
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] border border-[#D4AF37]/30 guild-card-glow p-6 md:p-7 overflow-hidden">
            <div aria-hidden className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at center, #D4AF37 0%, transparent 70%)" }} />

            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-[#D4AF37]/60" /> Best Match preview
            </div>

            <div className="text-[12.5px] text-zinc-300 leading-relaxed mb-5">
              Based on quiz answers, we line up your <span className="text-white font-semibold">top three matches</span> with plain-English reasons you'll click.
            </div>

            {/* Mock match cards */}
            <div className="space-y-3">
              {[
                { name: "Sarah K.", area: "Manchester · M14", reasons: ["Nervous-friendly", "Female", "Manual"], rating: "4.9" },
                { name: "James R.", area: "Manchester · M1", reasons: ["Patient", "Anxiety-aware", "Manual"], rating: "4.9" },
                { name: "Priya S.", area: "Manchester · M20", reasons: ["Calm pace", "Female", "Automatic"], rating: "4.8" },
              ].map((m, i) => (
                <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                      <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {m.area}
                      </div>
                    </div>
                    <div className="text-[11px] font-bold text-[#F3E5AB] inline-flex items-center gap-1">
                      <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" /> {m.rating}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.reasons.map((r, j) => (
                      <span key={j} className="text-[10.5px] font-semibold rounded-full px-2 py-0.5 bg-[#D4AF37]/12 text-[#F3E5AB] border border-[#D4AF37]/30">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Link to="/marketplace/match" className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#F3E5AB] hover:text-white transition">
              Get your real matches <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* AUDIENCE SWITCHER (small persistent chip row)                       */
/* ─────────────────────────────────────────────────────────────────── */

export function AudienceSwitcher({ current = "learner" }) {
  const links = [
    { id: "learner", label: "I'm a Learner", to: "/for-learners" },
    { id: "instructor", label: "I'm an Instructor", to: "/" },
    { id: "pdi", label: "I'm a PDI", to: "/for-pdis" },
  ];
  return (
    <section className="relative pt-2 pb-6 md:pb-8" data-testid="audience-switcher-row">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex-shrink-0">
            Not for you?
          </span>
          <div className="flex flex-wrap gap-2">
            {links.map((l) => {
              const active = l.id === current;
              return (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`switcher-${l.id}`}
                  className={`text-[12.5px] font-semibold rounded-full px-3.5 py-1.5 transition ${
                    active
                      ? "bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/40"
                      : "bg-white/[0.03] text-zinc-300 border border-white/10 hover:border-white/30 hover:text-white"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* LEARNER WORRIES                                                     */
/* ─────────────────────────────────────────────────────────────────── */

const WORRIES = [
  "I'm nervous and need a patient instructor",
  "I don't know if I want manual or automatic",
  "I'd prefer a female instructor",
  "I tried before and it didn't click",
  "How do I know they're properly qualified?",
  "I want to learn fast but safely",
];

function LearnerWorries() {
  const [sectionRef] = useSectionEngagement("learner_worries");
  return (
    <section ref={sectionRef} data-testid="learner-worries-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">Learner concerns</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            Worried about<br />picking the wrong instructor?
          </h2>
          <p className="mt-5 text-[15px] text-zinc-300 leading-relaxed max-w-md">
            You're not alone. Most learners pick blind, hope it clicks, and lose money + confidence when it doesn't. National Driving Guild was built so you don't have to.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-3">
            {WORRIES.map((w, i) => (
              <div key={i} data-testid={`learner-worry-${i}`} className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4 flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
                  <Quote className="w-3.5 h-3.5 text-rose-300" />
                </div>
                <div className="text-[13.5px] text-zinc-200 leading-snug">"{w}"</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* WHY LEARNERS — feature grid                                         */
/* ─────────────────────────────────────────────────────────────────── */

const FEATURES = [
  { icon: Compass, title: "Best Match quiz", body: "Tells us your confidence, vehicle preference, style, extras, location. We line up your top 3.", cta: { label: "Take the quiz", to: "/marketplace/match" } },
  { icon: Heart, title: "Nervous learner support", body: "Patient, calm, anxiety-aware instructors clearly tagged. We match the energy you need.", cta: null },
  { icon: Car, title: "Manual or automatic", body: "Both vehicle types, clearly labelled on every profile. No surprises after the first lesson.", cta: null },
  { icon: UserCheck, title: "Female instructor option", body: "Filter for female instructors near you in seconds — no need to ring around.", cta: null },
  { icon: BadgeCheck, title: "Verified & vetted", body: "Every NDG instructor is DVSA-licensed, ID-checked, and reviewed by real pupils.", cta: null },
  { icon: MessageSquare, title: "Easy booking & messaging", body: "Pre-filled first messages, built-in booking, and one place for everything.", cta: null },
];

function WhyLearners({ primaryHref }) {
  const [sectionRef] = useSectionEngagement("learner_why");
  return (
    <section ref={sectionRef} data-testid="learner-why-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="max-w-2xl mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">Why NDG</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            The whole search,<br />
            <span className="text-gold-gradient">solved properly.</span>
          </h2>
          <p className="mt-4 text-[15px] text-zinc-300 leading-relaxed">
            Real filters, real reviews, and a quiz that actually understands what you need.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, title, body, cta }, i) => (
            <div key={i} data-testid={`learner-feature-${i}`} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5 hover:border-[#D4AF37]/35 transition group">
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="font-display text-lg font-semibold text-white">{title}</div>
              <div className="mt-2 text-[13.5px] text-zinc-300 leading-relaxed">{body}</div>
              {cta && (
                <Link to={cta.to} data-testid={`learner-feature-${i}-cta`} className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#F3E5AB] hover:text-white transition">
                  {cta.label} <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HOW IT WORKS                                                        */
/* ─────────────────────────────────────────────────────────────────── */

const STEPS = [
  { n: "01", icon: Compass, title: "Take the quiz", body: "5 quick questions: confidence, vehicle, learning style, extras, postcode. 60 seconds." },
  { n: "02", icon: Sparkles, title: "Get matched", body: "Your top 3 instructors with plain-English reasons. Tap to view profile or message." },
  { n: "03", icon: Calendar, title: "Book your first lesson", body: "Send a pre-filled intro message, pick a slot, and pay. All in one place." },
];

function HowItWorks() {
  const [sectionRef] = useSectionEngagement("learner_how");
  return (
    <section ref={sectionRef} data-testid="learner-how-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            Quiz → Match → Book.<br />
            <span className="text-gold-gradient">That's the whole thing.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map(({ n, icon: Icon, title, body }, i) => (
            <div key={i} data-testid={`learner-step-${i}`} className="relative rounded-2xl border border-white/[0.07] bg-gradient-to-br from-[#0d0d0d] to-[#080808] p-6">
              <div className="text-[42px] font-display font-bold text-[#D4AF37]/20 leading-none">{n}</div>
              <div className="mt-3 w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-white">{title}</div>
              <div className="mt-2 text-[13.5px] text-zinc-300 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* SPECIALISMS STRIP                                                   */
/* ─────────────────────────────────────────────────────────────────── */

const SPECIALISM_TAGS = [
  "Manual", "Automatic", "Female instructor", "Male instructor",
  "Nervous learners", "Anxiety-aware", "Neurodivergent-friendly",
  "Patient & calm", "Fast-track", "Intensive courses", "Refresher",
  "Pass Plus", "Motorway lessons", "Night driving", "EV / Hybrid",
  "LGBTQ+ welcoming", "Disability confident", "Towing",
];

function SpecialismsStrip() {
  return (
    <section className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] p-6 md:p-10">
          <div className="text-center max-w-2xl mx-auto mb-7">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2 justify-center">
              <Sparkles className="w-3 h-3" /> Match by specialism
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-white">
              Filter for what actually matters to you.
            </h3>
            <p className="mt-3 text-[14px] text-zinc-300">
              Every NDG profile is tagged with verified specialisms — no more "we'll see how it goes."
            </p>
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {SPECIALISM_TAGS.map((s, i) => (
              <span key={i} data-testid={`learner-specialism-${i}`} className={`text-[12px] font-semibold rounded-full px-3 py-1.5 ${i % 4 === 0 ? "bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/35" : "bg-white/[0.04] text-zinc-300 border border-white/10"}`}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TESTIMONIAL                                                         */
/* ─────────────────────────────────────────────────────────────────── */

function LearnerTestimonial() {
  return (
    <section className="relative py-12 md:py-16">
      <div className="max-w-[900px] mx-auto px-4 md:px-8">
        <div className="rounded-3xl border border-white/[0.07] bg-white/[0.025] p-7 md:p-10">
          <div className="flex items-center gap-1 mb-5">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
          </div>
          <Quote className="w-7 h-7 text-[#D4AF37]/40 mb-3" />
          <p className="font-display text-xl md:text-2xl text-white leading-relaxed">
            "I'd already given up after a bad start with another school. The quiz matched me with a calm female instructor who understood my anxiety. Passed first time."
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#0a0a0a] border border-[#D4AF37]/30 flex items-center justify-center text-[#F3E5AB] font-bold text-sm">EM</div>
            <div>
              <div className="text-sm font-semibold text-white">Ellie M.</div>
              <div className="text-[12px] text-zinc-400">Passed in Leeds, Feb 2026</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* FINAL CTA                                                           */
/* ─────────────────────────────────────────────────────────────────── */

function FinalCta({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("learner_final_cta");
  return (
    <section ref={sectionRef} data-testid="learner-final-cta-section" className="relative py-12 md:py-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="relative rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] guild-card-glow p-8 md:p-14 overflow-hidden">
          <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at center, #D4AF37 0%, transparent 70%)" }} />
          <div className="relative max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-4 inline-flex items-center gap-2">
              <Zap className="w-3 h-3" /> Ready when you are
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white leading-tight">
              Get your top three matches<br />
              <span className="text-gold-gradient">in 60 seconds.</span>
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-zinc-300 leading-relaxed">
              Free, no signup needed to see your matches. Verified instructors only.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/marketplace/match" onClick={() => { markCtaClicked({ cta: "final_quiz" }); trackCta("learner_landing_final_cta", "Take the Best Match quiz", { destination: "/marketplace/match" }); }}>
                <Button data-testid="learner-final-quiz-btn" className="h-13 px-7 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto">
                  Take the Best Match quiz <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to={primaryHref}>
                <Button data-testid="learner-final-signup-btn" variant="outline" className="h-13 px-7 rounded-md bg-transparent border border-[#D4AF37]/45 hover:bg-[#D4AF37]/[0.06] text-white text-[14px] font-semibold w-full sm:w-auto">
                  Create a free learner account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TRUST FOOTER (mirrors Landing)                                      */
/* ─────────────────────────────────────────────────────────────────── */

const TRUST_ITEMS = [
  { icon: BadgeCheck, label: "DVSA-Verified Instructors" },
  { icon: Lock, label: "Secure & Private" },
  { icon: HeartHandshake, label: "Real Help. No Runaround." },
  { icon: Eye, label: "Transparent Reviews" },
];

function TrustFooter() {
  return (
    <section className="relative pb-16 md:pb-24">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div data-testid="trust-footer" className="border-t border-white/[0.06] pt-8 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-4 md:divide-x md:divide-white/[0.06]">
          {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
            <div key={i} data-testid={`trust-foot-${i}`} className="flex items-center justify-center gap-2.5 text-zinc-300 text-[12.5px] md:text-sm font-medium">
              <Icon className="w-4 h-4 text-[#D4AF37]" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.04] flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[11px] text-zinc-500">
          <div className="flex items-center gap-2.5">
            <NdgShield size={22} />
            <span className="font-condensed text-zinc-300 tracking-wider">NATIONAL DRIVING GUILD</span>
            <span className="hidden md:inline text-zinc-600">·</span>
            <span className="hidden md:inline">A professional home for learners and instructors.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-zinc-200 transition">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-200 transition">Privacy</Link>
            <Link to="/pricing?audience=learner" className="hover:text-zinc-200 transition">Pricing</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
